import React from "react";
import { Reveal, GridLines, RegMarks, Shell } from "../Components/Primitives";
import Seo, { SITE } from "../Components/Seo";
import Footer from "./Footer";

const SEO = {
  title: "Resume - Timi Gbenga, Front-end & Mobile Engineer",
  description:
    "The CV of Timi Gbenga: 3+ years of front-end and mobile engineering across React, Next.js, TypeScript, React Native and Tauri. Experience, skills, projects and education. Lagos, Nigeria.",
  path: "/resume",
  type: "profile",
  schema: {
    "@context": "https://schema.org",
    "@type": "ProfilePage",
    name: "Resume - Timi Gbenga",
    url: `${SITE}/resume`,
    about: { "@id": `${SITE}/#person` },
    significantLink: `${SITE}/resume.pdf`,
  },
};

/* The resume as a document: one sheet of paper laid on the plaster,
   with a real CV anatomy. Rail of credentials at the left, the
   narrative at the right. Apple restraint in the type and spacing,
   architectural rules holding the structure. */

const CONTACT = [
  {
    k: "Email",
    v: "gbengatimi166@gmail.com",
    href: "mailto:gbengatimi166@gmail.com",
  },
  { k: "Phone", v: "+234 704 346 4244", href: "tel:+2347043464244" },
  { k: "Location", v: "Lagos, Nigeria" },
  {
    k: "LinkedIn",
    v: "/in/timi-gbenga",
    href: "https://www.linkedin.com/in/timi-gbenga-ba782b304",
  },
  { k: "GitHub", v: "@Teemi2106", href: "https://github.com/Teemi2106" },
];

const SKILLS = [
  {
    k: "Frontend",
    v: "React.js, Next.js, TypeScript, JavaScript, HTML5, CSS3, Tailwind CSS, GSAP, Three.js",
  },
  { k: "Mobile & Desktop", v: "React Native, Expo, Tauri" },
  { k: "State", v: "Redux Toolkit, Zustand, Context API" },
  {
    k: "Backend & Data",
    v: "Supabase, Firebase, PostgreSQL, REST APIs, Flask, Upstash Redis",
  },
  { k: "Python & AI", v: "Python, PyTorch, scikit-learn, SHAP, Google Colab" },
  { k: "Tools", v: "Git, GitHub, Vercel, Postman, Figma" },
];

const EXPERIENCE = [
  {
    role: "Fullstack Developer",
    org: "BitForge",
    period: "2026 - Present",
    points: [],
  },
  {
    role: "Frontend Developer",
    org: "Adools Construct & Designs",
    period: "2025 - Present",
    points: [],
  },
  {
    role: "Frontend Developer, Freelance",
    org: "Independent",
    period: "2022 - Present",
    points: [],
  },
  {
    role: "Frontend Developer Intern",
    org: "HNG Internship",
    period: "2021",
    points: [
      "Built reusable React and TypeScript components used across multiple screens in a collaborative team environment.",
      "Participated in daily code reviews, debugging sessions and agile sprint workflows with a team of 10+ developers.",
    ],
  },
];

const PROJECTS = [
  {
    title: "myQuest",
    kind: "CBT Exam Preparation App",
    href: "https://play.google.com/store/apps/details?id=com.teemi2106.myquest",
    points: [
      "Built and published a cross-platform CBT prep app for Nigerian students in React Native and Expo. Live on Google Play with 1,000+ downloads and 700+ active users.",
      "Integrated AI-powered learning assistance, progress tracking, a scalable question bank and offline support.",
    ],
  },
  {
    title: "OZ Academy",
    kind: "School Management Platform",
    href: "",
    points: [
      "Full-stack platform with student enrolment, a learning management system, role-based dashboards for admins and instructors, and automated administrative workflows.",
    ],
  },
  {
    title: "EcoPack",
    kind: "Sustainable Packaging Discovery",
    href: "https://eco-pack-one.vercel.app/",
    points: [
      "Responsive web platform helping businesses discover and compare sustainable packaging options, with filtering and comparison tooling.",
    ],
  },
];

/* A ruled section heading inside the sheet. */
const Head = ({ children }) => (
  <div className="border-b border-rule-2 pb-2.5">
    <h2 className="label text-ink">{children}</h2>
  </div>
);

/* Bulleted line: a short rule rather than a dot, to match the drawing. */
const Point = ({ children }) => (
  <li className="grid grid-cols-[0.85rem_1fr] gap-2 text-[13.5px] leading-[1.6] tracking-[-0.008em] text-ink-70">
    <span className="pt-[0.6em]">
      <span className="block h-px w-2 bg-[var(--rule-2)]" />
    </span>
    <span>{children}</span>
  </li>
);

const Resume = () => (
  <main className="relative">
    <Seo {...SEO} />
    <GridLines count={5} />

    <div className="relative z-10">
      <section className="pt-28 md:pt-36 pb-16 md:pb-24">
        <Shell>
          <Reveal className="flex items-center gap-4 pb-8 md:pb-10">
            <span className="h-px w-10 bg-[var(--rule-2)]" />
            <span className="label">Curriculum Vitae</span>
          </Reveal>

          {/* ============ THE SHEET ============ */}
          <Reveal delay={80}>
            <article className="resume-sheet relative mx-auto max-w-[1060px] rounded-[26px] border border-rule bg-ground-raise px-7 py-10 shadow-[0_28px_60px_-32px_rgba(22,22,26,0.18),0_2px_8px_-4px_rgba(22,22,26,0.06)] md:px-14 md:py-14">
              <RegMarks inset={16} />

              {/* --- Masthead --- */}
              <header className="border-b border-ink pb-7">
                <div className="flex flex-wrap items-end justify-between gap-x-8 gap-y-3">
                  <h1
                    className="display leading-[0.9]"
                    style={{ fontSize: "clamp(2.5rem, 6vw, 4.25rem)" }}
                  >
                    Timi Gbenga
                  </h1>
                  <p className="label pb-1.5 text-ink-70">
                    Frontend &middot; Mobile &middot; Fullstack
                  </p>
                </div>
              </header>

              {/* --- Body: rail + narrative --- */}
              <div className="grid gap-10 pt-9 md:grid-cols-[minmax(0,0.34fr)_minmax(0,0.66fr)] md:gap-14">
                {/* ---------- LEFT RAIL ---------- */}
                <aside className="space-y-9">
                  <section>
                    <Head>Contact</Head>
                    <dl className="pt-4">
                      {CONTACT.map((c) => (
                        <div key={c.k} className="pb-3 last:pb-0">
                          <dt className="label pb-0.5">{c.k}</dt>
                          <dd className="text-[13.5px] tracking-[-0.01em] text-ink break-words">
                            {c.href ? (
                              <a
                                href={c.href}
                                target={
                                  c.href.startsWith("http")
                                    ? "_blank"
                                    : undefined
                                }
                                rel="noopener noreferrer"
                                className="link-draw"
                              >
                                {c.v}
                              </a>
                            ) : (
                              c.v
                            )}
                          </dd>
                        </div>
                      ))}
                    </dl>
                  </section>

                  <section>
                    <Head>Technical Skills</Head>
                    <dl className="pt-4">
                      {SKILLS.map((s) => (
                        <div key={s.k} className="pb-3.5 last:pb-0">
                          <dt className="label pb-1">{s.k}</dt>
                          <dd className="text-[13px] leading-[1.55] tracking-[-0.008em] text-ink-70">
                            {s.v}
                          </dd>
                        </div>
                      ))}
                    </dl>
                  </section>

                  <section>
                    <Head>Education</Head>
                    <div className="pt-4">
                      <p className="text-[14px] font-medium tracking-[-0.02em] text-ink">
                        B.Sc. Software Engineering
                      </p>
                      <p className="label pt-1">
                        Bowen University, Iwo &middot; 2026
                      </p>
                      <p className="pt-2.5 text-[13px] leading-[1.55] text-ink-70">
                        CGPA 4.1 / 5.0
                      </p>
                      <p className="pt-2 text-[13px] leading-[1.55] text-ink-70">
                        <span className="text-ink">Final year project:</span>{" "}
                        Biometrix, a deep-learning multimodal biometric
                        verification system using facial recognition and in-air
                        signatures.
                      </p>
                      <p className="pt-2 text-[13px] leading-[1.55] text-ink-70">
                        Human-Computer Interaction, Computer Networks, Software
                        Engineering, Machine Learning, Big Data, IoT.
                      </p>
                      <p className="pt-2 text-[13px] leading-[1.55] text-ink-70">
                        Member, NACOS, the Nigerian Association of Computing
                        Students.
                      </p>
                    </div>
                  </section>

                  <section>
                    <Head>Languages</Head>
                    <p className="pt-4 text-[13.5px] tracking-[-0.01em] text-ink-70">
                      English (fluent)
                    </p>
                  </section>
                </aside>

                {/* ---------- RIGHT: NARRATIVE ---------- */}
                <div className="space-y-9">
                  <section>
                    <Head>Profile</Head>
                    <p className="pt-4 text-[14.5px] leading-[1.65] tracking-[-0.01em] text-ink-70">
                      Frontend developer with 3+ years shipping production web
                      and mobile apps in React, Next.js, TypeScript and React
                      Native. Built myQuest, a CBT prep app with 1,000+
                      downloads and 700+ active users, and currently developing
                      Reply, a WhatsApp AI assistant for Nigerian SMBs. 10+
                      client projects delivered across education, e-commerce and
                      real estate, from Figma through to deployment.
                    </p>
                  </section>

                  <section>
                    <Head>Experience</Head>
                    <div className="pt-1">
                      {EXPERIENCE.map((job) => (
                        <div
                          key={`${job.role}-${job.org}`}
                          className="border-b border-rule py-5 last:border-b-0 last:pb-0"
                        >
                          <div className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1">
                            <h3 className="text-[16.5px] font-medium tracking-[-0.025em] text-ink">
                              {job.role}
                            </h3>
                            <span className="label tabular-nums">
                              {job.period}
                            </span>
                          </div>
                          <p className="label pt-1 text-ink-70">{job.org}</p>
                          {job.points.length > 0 && (
                            <ul className="space-y-2 pt-3.5">
                              {job.points.map((p) => (
                                <Point key={p}>{p}</Point>
                              ))}
                            </ul>
                          )}
                        </div>
                      ))}
                    </div>
                  </section>

                  <section>
                    <Head>Selected Projects</Head>
                    <div className="pt-1">
                      {PROJECTS.map((p) => (
                        <div
                          key={p.title}
                          className="border-b border-rule py-5 last:border-b-0 last:pb-0"
                        >
                          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                            <a
                              href={p.href}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="link-draw text-[16.5px] font-medium tracking-[-0.025em] text-ink"
                            >
                              {p.title}
                            </a>
                            <span className="label">{p.kind}</span>
                          </div>
                          <ul className="space-y-2 pt-3.5">
                            {p.points.map((pt) => (
                              <Point key={pt}>{pt}</Point>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </section>
                </div>
              </div>
            </article>
          </Reveal>
        </Shell>
      </section>

      <Footer />
    </div>
  </main>
);

export default Resume;
